import { useContext, useMemo } from 'react'

import { getOfferImageId, getSavedDetailsForOffer, mapExperienceTicketSelections } from 'tripPlanner/components/Bookmark/BookmarkButton/utils'
import { useEditableTrips } from 'tripPlanner/hooks/api/trip'
import { EmptyArray } from 'lib/array/arrayUtils'
import SavePrompt from './SavePrompt'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import type { ExperienceBookmarkPayload } from 'tripPlanner/api/bookmark/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import ExperienceOfferPageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import { useCurrentExperiencePageTicketOptions } from 'hooks/ExperiencePage/useCurrentExperiencePageTicketOptions'

interface Props {
  offer: App.ExperienceOffer
}

function SavePromptExperience({ offer }: Props) {
  const experienceOfferPageState = useContext(ExperienceOfferPageStateContext)
  const allTicketOptions = useCurrentExperiencePageTicketOptions(
    offer.id,
    experienceOfferPageState,
  )

  const { data, isSuccess: tripsFetched } = useEditableTrips()
  const trips = data ?? EmptyArray as Array<BasicTrip>

  const details = getSavedDetailsForOffer(trips, offer.id)
  const isSaved = details.length > 0

  const newTripName = useNewTripNameFromOffer(offer)

  const bookmarkPayload = useMemo<ExperienceBookmarkPayload>(() => ({
    type: 'experience',
    code: offer.id,
    languageId: experienceOfferPageState.language?.id,
    pickupPointId: experienceOfferPageState.pickupPoint?.id,
    redemptionLocationId: experienceOfferPageState.redemptionLocation?.id,
    startDate: experienceOfferPageState.date,
    startTime: experienceOfferPageState.time,
    tickets: mapExperienceTicketSelections(
      experienceOfferPageState,
      allTicketOptions,
    ),
  }), [allTicketOptions, experienceOfferPageState, offer.id])

  if (isSaved || !tripsFetched) {
    return null
  }

  return <SavePrompt
    offerId={offer.id}
    offerTypeLabel={offer.productType === 'transfer' ? 'transfer' : 'experience'}
    bookmarkPayload={bookmarkPayload}
    newTripName={newTripName}
    offerImageId={getOfferImageId(offer)}
    trips={trips}
  />
}

export default SavePromptExperience
